'use client'

import { MoveLeft } from 'lucide-react'

import { LogoMark } from '../elements/Logo'
import { LanguageSwitcher } from '../i18n/LanguageSwitcher'
import { useT } from '../i18n/LocaleProvider'

/**
 * The frame every auth screen sits in: the form on the left, a mock of the page
 * being signed up for on the right.
 *
 * The mock is not decoration. Sign-up asks for a handle, a name and a bio with
 * no obvious payoff, and the panel answers "what am I filling this in for?" by
 * assembling the answer as it is typed. Below `lg` it is dropped rather than
 * stacked — on a phone it would push the form itself below the fold, which is
 * the one thing the screen exists to show.
 */
export function AuthShell({ children, aside }: { children: React.ReactNode, aside?: React.ReactNode }) {
  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <section className="relative flex min-w-0 items-center justify-center px-6 pb-12 pt-24 sm:px-8">
        <div className="absolute left-6 top-7 sm:left-8">
          <LogoMark size={36} priority />
        </div>
        <div className="absolute right-6 top-6 sm:right-8">
          <LanguageSwitcher />
        </div>
        <div className="flex w-full max-w-110 flex-col gap-6">
          {children}
        </div>
      </section>

      { aside && (
        <aside className="hidden min-w-0 items-center justify-center border-l border-border bg-bg px-8 py-12 lg:flex">
          {aside}
        </aside>
      ) }
    </div>
  )
}

/**
 * The title block, and the way back out of a step.
 *
 * The back control is a button rather than a link because every screen that
 * shows one is mid-flow — going back means dropping a half-entered code or
 * address, which is state this component's parent owns.
 */
export function AuthHeader({ title, subtitle, backLabel, onBack, compact = false }: {
  title: string
  subtitle?: string
  backLabel?: string
  onBack?: () => void
  /**
   * Steps down the title so something below it can be the focal point. Used by
   * the closing screen, where the link the user just earned outranks the
   * congratulation — at the full size the two compete, and on a narrow screen
   * the heading wins, which is the wrong way round.
   */
  compact?: boolean
}) {
  return (
    <div className="flex flex-col gap-6">
      { onBack && backLabel && (
        <button
          type="button"
          onClick={onBack}
          className="flex cursor-pointer items-center gap-2 self-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground lg:self-start"
        >
          <MoveLeft className="size-4" aria-hidden="true" />
          {backLabel}
        </button>
      ) }
      {/* Centred until the preview panel comes back at lg. Below it the form is
          the whole screen and reads better balanced; beside the panel it is one
          column of a pair, and centring it would break the shared left edge. */}
      <div className="flex flex-col gap-2 text-center lg:text-left">
        <h1 className={`font-gilroy font-bold leading-tight tracking-tight text-foreground ${compact ? 'text-xl' : 'text-[26px]'}`}>{title}</h1>
        { subtitle && <p className="text-sm font-medium leading-relaxed text-muted-foreground">{subtitle}</p> }
      </div>
    </div>
  )
}

/** The "or" rule between the provider buttons and the email form. */
export function AuthDivider() {
  const t = useT()
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center" aria-hidden="true">
        <div className="w-full border-t border-border" />
      </div>
      <div className="relative flex justify-center text-sm font-medium">
        <span className="bg-background px-5 text-muted-foreground">{t('common.or')}</span>
      </div>
    </div>
  )
}

/** First letters of the first two words — "Ариунаа Батсайхан" becomes "АБ". */
export function initialsOf(value?: string | null): string {
  if(!value) return ''
  return value.trim().split(/\s+/).slice(0, 2).map(part => part[0] ?? '').join('').toUpperCase()
}
